// START: GitHub Copilot Generated Code
//inspired by https://github.com/mandarini/swipe-carousel/tree/master?tab=readme-ov-file

import React, { useCallback, useEffect, useRef } from 'react';

import { LogVerbose } from '../../../services/utilities';

interface SwipeableProps {
    children?: React.ReactNode | React.ReactNode[];
    onTouch?: () => void;
    onSwipedLeft?: (deltaX?: number) => void;
    onSwipedRight?: (deltaX?: number) => void;
    onSwipedUp?: (deltaY?: number) => void;
    onSwipedDown?: (deltaY?: number) => void;
    onSwiping?: ({ deltaX, deltaY }: { deltaX: number; deltaY: number }) => void;
    swipeableClass?: string;
    threshold?: number; // minimum distance in pixels to register a swipe
}

interface SwipeStart {
    xStart: number;
    yStart: number;
}

export const Swipeable = ({ children, onSwipedLeft, onSwipedRight, onSwipedUp, onSwipedDown, onSwiping, onTouch, swipeableClass, threshold = 50 }: SwipeableProps) => {

    const elRef = useRef<HTMLDivElement>(null);

    // Use refs instead of state for touch tracking — refs update synchronously,
    // preventing iOS intermittent failures caused by React batching state updates
    // between rapid touch events.
    const swipeStartRef = useRef<SwipeStart | undefined>(undefined);
    const swipingRef = useRef<boolean>(false);

    // Store callbacks in refs so the native listeners always see the latest props
    // without needing to detach/reattach on every render.
    const callbacksRef = useRef({ onSwipedLeft, onSwipedRight, onSwipedUp, onSwipedDown, onSwiping, onTouch });
    callbacksRef.current = { onSwipedLeft, onSwipedRight, onSwipedUp, onSwipedDown, onSwiping, onTouch };

    const handleTouchStart = useCallback((e: TouchEvent) => {
        const touch = e.touches[0];
        swipeStartRef.current = { xStart: touch.clientX, yStart: touch.clientY };
        if (callbacksRef.current.onTouch) {
            callbacksRef.current.onTouch();
        }
    }, []);

    const handleTouchMove = useCallback((e: TouchEvent) => {
        if (e.changedTouches && e.changedTouches.length) {
            if (!swipingRef.current)
                swipingRef.current = true;
            if (swipeStartRef.current) {
                const changedTouch = e.changedTouches[0];
                const deltaX = changedTouch.clientX - swipeStartRef.current.xStart;
                const deltaY = changedTouch.clientY - swipeStartRef.current.yStart;
                LogVerbose(`Swiping... deltaX=${deltaX}, deltaY=${deltaY}`);
                if (callbacksRef.current.onSwiping)
                    callbacksRef.current.onSwiping({ deltaX, deltaY });
            }
            else {
                LogVerbose('Swiping...');
            }
        }
    }, []);

    const handleTouchEnd = useCallback((e: TouchEvent) => {
        if (swipingRef.current && swipeStartRef.current) {
            const changedTouch = e.changedTouches[0];
            const deltaX = Math.abs(changedTouch.clientX - swipeStartRef.current.xStart);
            const deltaY = Math.abs(changedTouch.clientY - swipeStartRef.current.yStart);
            const cbs = callbacksRef.current;

            if (changedTouch.clientX > swipeStartRef.current.xStart && deltaX >= threshold) {
                LogVerbose('Swiped Right');
                if (cbs.onSwipedRight)
                    cbs.onSwipedRight(changedTouch.clientX - swipeStartRef.current.xStart);
            }

            if (changedTouch.clientX < swipeStartRef.current.xStart && deltaX >= threshold) {
                LogVerbose('Swiped Left');
                if (cbs.onSwipedLeft)
                    cbs.onSwipedLeft(swipeStartRef.current.xStart - changedTouch.clientX);
            }
            if (changedTouch.clientY > swipeStartRef.current.yStart && deltaY >= threshold) {
                LogVerbose('Swiped Down');
                if (cbs.onSwipedDown)
                    cbs.onSwipedDown(changedTouch.clientY - swipeStartRef.current.yStart);
            }

            if (changedTouch.clientY < swipeStartRef.current.yStart && deltaY >= threshold) {
                LogVerbose('Swiped Up');
                if (cbs.onSwipedUp)
                    cbs.onSwipedUp(swipeStartRef.current.yStart - changedTouch.clientY);
            }
        }
        swipingRef.current = false;
        swipeStartRef.current = undefined;
    }, [threshold]);

    const handleTouchCancel = useCallback(() => {
        swipingRef.current = false;
        swipeStartRef.current = undefined;
    }, []);

    // Attach native touch event listeners directly on the DOM element.
    // React 17+ delegates events to the #root container, NOT to individual
    // DOM nodes. iOS Safari may skip touch-event delivery for elements that
    // have no native listeners in their ancestor chain — which is exactly
    // what Eruda fixes accidentally by adding global listeners to `document`.
    // Using native addEventListener ensures the element itself is recognized
    // as interactive by Safari's hit-testing engine.
    useEffect(() => {
        const el = elRef.current;
        if (!el) return;

        el.addEventListener('touchstart', handleTouchStart, { passive: true });
        el.addEventListener('touchmove', handleTouchMove, { passive: true });
        el.addEventListener('touchend', handleTouchEnd, { passive: true });
        el.addEventListener('touchcancel', handleTouchCancel, { passive: true });

        return () => {
            el.removeEventListener('touchstart', handleTouchStart);
            el.removeEventListener('touchmove', handleTouchMove);
            el.removeEventListener('touchend', handleTouchEnd);
            el.removeEventListener('touchcancel', handleTouchCancel);
        };
    }, [handleTouchStart, handleTouchMove, handleTouchEnd, handleTouchCancel]);

    return (
        <div
            ref={elRef}
            className={swipeableClass}
            style={{ cursor: 'pointer', touchAction: 'pan-y' }}
        >
            {children}
        </div>
    );
// END: GitHub Copilot Generated Code
};